import React, { useMemo, useCallback } from 'react';
import { getRoute } from '@utils';
import { useQuery } from '@apollo/client';
import {
  LastWhimProductsQueryResponse,
  LastWhimProductsQuery,
  LastWhimProductsParams,
} from '@queries/products/queries/lastWhimProducts';
import { LastWhimContainer } from './styles';
import { PageWrapper } from '@components/PageWrapper';
import { BackgroundGlobalStyle } from '@components/PageWrapper';
import { SimpleHeader } from '@components/Header/SimpleHeader';
import { CheckoutCardsContainer, HorizontalContainer } from '../styles';
import { OrderSummary } from '../components/OrderSummary';
import { useRouter } from 'next/router';
import { FREE_SHIPPING_MIN_AMOUNT } from '@config/constants';
import { useAuth } from '@hooks/useAuth';
import { DELIVERY_COST } from '@config/constants';
import { useOrderState } from '@hooks/useOrderState';
import { usePermissions } from '@hooks/usePermissions';
import { FreeDeliveryProgressCard } from '@components/FreeDeliveryProgressCard';
import {
  LastWhimProductCard,
  LastWhimProductCardSkeleton,
} from './components/LastWhimProductCard';
import { useWindowSize } from '@hooks/useWindowSize';
import { CheckoutTitle } from '../components/Title';
import { useEventTracking } from '@hooks/useEventTracking';
import { ScreenEventType } from 'src/validators/Events/Screen';

export function LastWhimPage() {
  const { isDesktop } = useWindowSize();
  const { track } = useEventTracking();
  const router = useRouter();
  const { appUser: user } = useAuth();
  const { order: currentOrder } = useOrderState();
  const { userCan } = usePermissions();
  const isLocalVendor = userCan('LOCAL_VENDOR');

  const { data: lastWhimData, loading: lastWhimLoading } = useQuery<
    LastWhimProductsQueryResponse,
    LastWhimProductsParams
  >(LastWhimProductsQuery(), {
    variables: {
      params: { orderUuid: currentOrder?.uuid ?? '' },
    },
    skip: !currentOrder?.uuid,
  });
  void track(ScreenEventType.ScreenLastWhimView);

  const subTotal = useMemo(() => {
    return currentOrder?.orderDetails.reduce(
      (acc, curr) =>
        acc +
        (curr.product.discount?.price ?? curr.product.price) * curr.quantity,
      0,
    );
  }, [currentOrder?.orderDetails]);

  const { deliveryCost, oldDeliveryCost } = useMemo(() => {
    const hasDeliveryCoupons = user?.deliveryCoupons?.count > 0;
    if (isLocalVendor || subTotal >= FREE_SHIPPING_MIN_AMOUNT) {
      return {
        deliveryCost: 0,
        oldDeliveryCost: 0,
      };
    }
    if (hasDeliveryCoupons) {
      return {
        deliveryCost: 0,
        oldDeliveryCost: DELIVERY_COST,
      };
    }
    return {
      deliveryCost: DELIVERY_COST,
      oldDeliveryCost: DELIVERY_COST,
    };
  }, [user?.deliveryCoupons?.count, isLocalVendor, subTotal]);

  const handleNext = useCallback(() => {
    void router.push(getRoute('checkoutDelivery').toString());
  }, [router]);

  const lastWhimFilteredData = useMemo(() => {
    return lastWhimData?.lastWhimProducts?.filter((product) =>
      ['AVAILABLE_24_HRS', 'AVAILABLE'].includes(product?.availability?.status),
    );
  }, [lastWhimData?.lastWhimProducts, lastWhimLoading]);

  const title = '¿Olvidaste algo?';
  const headerTitle = isDesktop ? '' : title;

  return (
    <>
      <BackgroundGlobalStyle mobileColor="gray" />
      <SimpleHeader
        backButton
        homeButton={isDesktop}
        title={headerTitle}
        color="white"
      />
      <PageWrapper addHeaderPadding={false} disableMobilePadding>
        <HorizontalContainer>
          <CheckoutCardsContainer>
            {isDesktop && <CheckoutTitle title={title} />}
            <LastWhimContainer>
              <FreeDeliveryProgressCard
                subTotalCart={subTotal}
                loading={lastWhimLoading}
              />
              {lastWhimLoading ? (
                <>
                  <LastWhimProductCardSkeleton />
                  <LastWhimProductCardSkeleton />
                  <LastWhimProductCardSkeleton />
                </>
              ) : (
                lastWhimFilteredData?.map((product) => (
                  <LastWhimProductCard key={product.id} product={product} />
                ))
              )}
            </LastWhimContainer>
          </CheckoutCardsContainer>
          {isDesktop && (
            <OrderSummary
              variant="desktop"
              isDelivery={!isLocalVendor}
              deliveryCost={deliveryCost}
              oldDeliveryCost={oldDeliveryCost}
              showInsurance={false}
              showMekiCredits={false}
              handleNext={handleNext}
              buttonText="Continuar al pago"
              loading={lastWhimLoading}
            />
          )}
        </HorizontalContainer>
      </PageWrapper>
      {!isDesktop && (
        <OrderSummary
          variant="fixed-bottom"
          isDelivery={!isLocalVendor}
          deliveryCost={deliveryCost}
          oldDeliveryCost={oldDeliveryCost}
          showInsurance={false}
          showMekiCredits={false}
          handleNext={handleNext}
          buttonText="Continuar al pago"
          loading={lastWhimLoading}
        />
      )}
    </>
  );
}
